import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useI18n } from '../lib/i18n';
import { dictionary } from '../lib/content';
import { Brain, TrendingUp, Target, Dna, CloudLightning, Package, HardDrive, Cpu, Waves, X, ArrowRight } from 'lucide-react';
const icons: Record<string, React.ReactNode> = {
  ia: <Brain size={24} data-id="element-8" />,
  mercados: <TrendingUp size={24} data-id="element-9" />,
  predictivos: <Target size={24} data-id="element-10" />,
  neurociencia: <Dna size={24} data-id="element-11" />,
  clima: <CloudLightning size={24} data-id="element-12" />,
  logistica: <Package size={24} data-id="element-13" />,
  compresion: <HardDrive size={24} data-id="element-14" />,
  simbolica: <Cpu size={24} data-id="element-15" />,
  resonancia: <Waves size={24} data-id="element-16" />
};
export function Applications() {
  const {
    t,
    lang
  } = useI18n();
  const [selectedApp, setSelectedApp] = useState<any | null>(null);
  const items = dictionary[lang].apps.items;
  return <section className="py-32 relative border-t border-black/[0.06] bg-white" data-id="element-17">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-18">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-16" data-id="element-19">
          <h2 className="font-display text-4xl md:text-5xl text-[#0A0A0F] mb-4" data-id="element-20">
            {t('apps.title')}
          </h2>
          <div className="h-px w-24 bg-violet-electric/60" data-id="element-21" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-22">
          {items.map((item: any, i: number) => <motion.div key={item.id} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.05
        }} onClick={() => setSelectedApp(item)} className="group bg-white border border-black/[0.06] p-8 rounded-2xl cursor-pointer hover:border-violet-electric/40 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 flex flex-col h-full" data-id="element-23">
              <div className="text-violet-electric mb-6 opacity-90 group-hover:scale-110 transition-all duration-300" data-id="element-24">
                {icons[item.id]}
              </div>
              <h3 className="text-xl font-medium text-[#0A0A0F] mb-3" data-id="element-25">
                {item.title}
              </h3>
              <p className="text-sm text-[#4A4A55] leading-relaxed mb-8 flex-grow" data-id="element-26">
                {item.desc}
              </p>
              <div className="flex items-center text-xs font-mono text-violet-electric uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity transform translate-y-2 group-hover:translate-y-0" data-id="element-27">
                {t('apps.viewCase')}
              </div>
            </motion.div>)}
        </div>
      </div>

      <AnimatePresence data-id="element-28">
        {selectedApp && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6" data-id="element-29">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setSelectedApp(null)} className="absolute inset-0 bg-[#0A0A0F]/40 backdrop-blur-sm" data-id="element-30" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="relative w-full max-w-2xl bg-white border border-black/[0.08] rounded-2xl p-8 md:p-12 shadow-2xl overflow-y-auto max-h-[90vh]" data-id="element-31">
              <button onClick={() => setSelectedApp(null)} className="absolute top-6 right-6 text-[#6A6A75] hover:text-[#0A0A0F] transition-colors" data-id="element-32">
                <X size={24} data-id="element-33" />
              </button>

              <div className="text-violet-electric mb-6" data-id="element-34">
                {icons[selectedApp.id]}
              </div>
              <h3 className="font-display text-3xl md:text-4xl text-[#0A0A0F] mb-6" data-id="element-35">
                {selectedApp.title}
              </h3>

              <p className="text-lg text-[#3A3A45] font-light leading-relaxed mb-8" data-id="element-36">
                {selectedApp.fullDesc}
              </p>

              <div className="mb-8 p-6 rounded-xl bg-[#F4F4F6] border border-black/[0.05]" data-id="element-37">
                <h4 className="text-sm font-mono text-[#4A4A55] uppercase tracking-widest mb-4" data-id="element-38">
                  {t('apps.useCases')}
                </h4>
                <ul className="space-y-3" data-id="element-39">
                  {selectedApp.cases.map((useCase: string, idx: number) => <li key={idx} className="flex items-start gap-3 text-[#2A2A35]" data-id="element-40">
                      <ArrowRight size={16} className="text-violet-electric mt-1 flex-shrink-0" data-id="element-41" />
                      <span data-id="element-42">{useCase}</span>
                    </li>)}
                </ul>
              </div>

              <div className="h-32 rounded-xl border border-black/[0.06] bg-[#FAFAFA] flex items-center justify-center relative overflow-hidden" data-id="element-43">
                <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-violet-200 to-transparent" data-id="element-44" />
                <div className="flex items-center gap-4 text-[#4A4A55] font-mono text-xs z-10" data-id="element-45">
                  <div className="px-3 py-1 border border-black/[0.1] bg-white rounded" data-id="element-46">
                    Input
                  </div>
                  <ArrowRight size={14} data-id="element-47" />
                  <div className="px-3 py-1 border border-violet-electric/40 bg-violet-100 text-violet-deep rounded" data-id="element-48">
                    λ-Latencia
                  </div>
                  <ArrowRight size={14} data-id="element-49" />
                  <div className="px-3 py-1 border border-black/[0.1] bg-white rounded" data-id="element-50">
                    Resonancia
                  </div>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </section>;
}